import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, User, Mail, Shield, Trash2, ExternalLink, AlertCircle, CheckCircle, Loader2, Monitor, PanelRight, Maximize2, Key, ChevronDown, ChevronUp, Eye, EyeOff, Copy, HelpCircle, Check, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../stores/auth.store';
import { useUiStore, useIsMobile, ReadingPaneMode } from '../stores/ui.store';
import { mailApi, authApi } from '../api/client';
import api from '../api/client';
import Logo from '../components/shared/Logo';
import { ProviderSettings } from '../features/ai/components/ProviderSettings';
import { SummaryPromptSettings } from '../features/ai/components/SummaryPromptSettings';

// OAuth Config type
interface OAuthConfig {
  id: string;
  provider: string;
  clientId: string;
  clientIdMasked: string;
  redirectUri: string | null;
  tenantId: string | null;
  isActive: boolean;
  createdAt: string;
}

// Instruction step type
interface InstructionStep {
  step: number;
  title: string;
  description: string;
  link?: string;
  copyValue?: string;
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user, logout } = useAuthStore();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Redirect URIs for OAuth setup
  const baseUrl = window.location.origin;
  const microsoftRedirectUri = `${baseUrl}/api/auth/callback/microsoft`;
  const googleRedirectUri = `${baseUrl}/api/auth/callback/google`;
  
  // OAuth config state
  const [expandedProvider, setExpandedProvider] = useState<'google' | 'microsoft' | null>(null);
  const [showInstructions, setShowInstructions] = useState<'google' | 'microsoft' | null>(null);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [oauthForms, setOauthForms] = useState<Record<string, {
    clientId: string;
    clientSecret: string;
    tenantId?: string;
  }>>({});
  
  // Display settings
  const readingPaneMode = useUiStore((state) => state.readingPaneMode);
  const setReadingPaneMode = useUiStore((state) => state.setReadingPaneMode);
  const isMobile = useIsMobile();
  
  // Show success/error from OAuth callback
  const success = searchParams.get('success');
  const error = searchParams.get('error');
  
  // Fetch mail accounts
  const { data: accountsData, isLoading: accountsLoading } = useQuery({
    queryKey: ['mail-accounts'],
    queryFn: () => mailApi.getAccounts(),
  });
  
  const accounts = accountsData?.accounts || [];
  
  // Fetch OAuth configs
  const { data: oauthConfigsData, isLoading: oauthConfigsLoading } = useQuery({
    queryKey: ['oauth-configs'],
    queryFn: () => api.get('user/oauth-configs').json<{ configs: OAuthConfig[] }>(),
  });
  
  const oauthConfigs = oauthConfigsData?.configs || [];
  
  // OAuth config mutations
  const saveOAuthConfigMutation = useMutation({
    mutationFn: (data: { provider: string; clientId: string; clientSecret: string; tenantId?: string }) =>
      api.post('user/oauth-configs', { json: data }).json(),
    onSuccess: (_, variables) => {
      toast.success(`${variables.provider === 'google' ? 'Google' : 'Microsoft'} OAuth konfiguriert!`);
      queryClient.invalidateQueries({ queryKey: ['oauth-configs'] });
      setExpandedProvider(null);
      setOauthForms(prev => ({ ...prev, [variables.provider]: { clientId: '', clientSecret: '', tenantId: '' } }));
    },
    onError: (error: any) => {
      const message = error?.response?.status === 400 
        ? 'Ungültige Eingabe. Bitte überprüfe die Credentials.'
        : 'Fehler beim Speichern';
      toast.error(message);
    },
  });
  
  const deleteOAuthConfigMutation = useMutation({
    mutationFn: (provider: string) => api.delete(`user/oauth-configs/${provider}`).json(),
    onSuccess: (_, provider) => {
      toast.success(`${provider === 'google' ? 'Google' : 'Microsoft'} OAuth gelöscht`);
      queryClient.invalidateQueries({ queryKey: ['oauth-configs'] });
    },
    onError: (error: any) => {
      const message = error?.response?.json?.message || 'Fehler beim Löschen';
      toast.error(message);
    },
  });
  
  // Fetch instructions
  const { data: googleInstructions } = useQuery({
    queryKey: ['oauth-instructions', 'google'],
    queryFn: () => api.get('user/oauth-configs/instructions/google').json<{ steps: InstructionStep[]; title: string }>(),
    enabled: showInstructions === 'google',
  });
  
  const { data: microsoftInstructions } = useQuery({
    queryKey: ['oauth-instructions', 'microsoft'],
    queryFn: () => api.get('user/oauth-configs/instructions/microsoft').json<{ steps: InstructionStep[]; title: string; tenantOptions: any[] }>(),
    enabled: showInstructions === 'microsoft',
  });
  
  // Copy to clipboard helper
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('In Zwischenablage kopiert');
  };
  
  // Disconnect account mutation
  const disconnectMutation = useMutation({
    mutationFn: (accountId: string) => mailApi.disconnectAccount(accountId),
    onSuccess: () => {
      toast.success('Account getrennt');
      queryClient.invalidateQueries({ queryKey: ['mail-accounts'] });
    },
    onError: () => {
      toast.error('Fehler beim Trennen');
    },
  });
  
  // Delete account mutation
  const deleteAccountMutation = useMutation({
    mutationFn: () => authApi.deleteAccount(),
    onSuccess: async () => {
      toast.success('Account gelöscht');
      await logout();
      navigate('/');
    },
    onError: () => {
      toast.error('Fehler beim Löschen');
    },
  });
  
  // OAuth connect mutations
  const connectMicrosoftMutation = useMutation({
    mutationFn: () => api.post('auth/oauth/microsoft/init').json<{ url: string }>(),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error: any) => {
      if (error?.response?.status === 500) {
        toast.error('Microsoft OAuth nicht konfiguriert. Bitte OAuth-Credentials in .env eintragen.');
      } else {
        toast.error('Fehler beim Verbinden mit Microsoft');
      }
    },
  });
  
  const connectGoogleMutation = useMutation({
    mutationFn: () => api.post('auth/oauth/google/init').json<{ url: string }>(),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error: any) => {
      if (error?.response?.status === 500) {
        toast.error('Google OAuth nicht konfiguriert. Bitte OAuth-Credentials in .env eintragen.');
      } else {
        toast.error('Fehler beim Verbinden mit Google');
      }
    },
  });
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-bg-primary via-bg-elevated to-bg-primary">
      {/* Header */}
      <header className="border-b border-border-default">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-bg-surface-hover rounded-lg text-text-muted hover:text-text-secondary transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-medium text-text-primary">Einstellungen</h1>
        </div>
      </header>
      
      <main className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {/* Success/Error messages */}
        {success === 'account_connected' && (
          <div className="p-4 bg-success/10 border border-success/20 rounded-lg flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-success" />
            <span className="text-success">E-Mail-Account erfolgreich verbunden!</span>
          </div>
        )}
        {error && (
          <div className="p-4 bg-error/10 border border-error/20 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-error" />
            <span className="text-error">
              {error === 'oauth_failed' && 'Verbindung fehlgeschlagen. Bitte versuche es erneut.'}
              {error === 'max_accounts' && 'Maximale Anzahl an Accounts erreicht.'}
              {error === 'invalid_state' && 'Ungültige Sitzung. Bitte versuche es erneut.'}
              {error === 'no_refresh_token' && 'Google hat keinen Refresh-Token geliefert. Bitte versuche es erneut.'}
              {!['oauth_failed', 'max_accounts', 'invalid_state', 'no_refresh_token'].includes(error) && `Fehler: ${error}`}
            </span>
          </div>
        )}
        
        {/* Profile Section */}
        <section className="bg-bg-surface border border-border-default rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <User className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-medium text-text-primary">Profil</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-text-muted mb-1">Name</label>
              <p className="text-text-primary">{user?.displayName}</p>
            </div>
            <div>
              <label className="block text-sm text-text-muted mb-1">E-Mail</label>
              <p className="text-text-primary">{user?.email}</p>
            </div>
            <div>
              <label className="block text-sm text-text-muted mb-1">Status</label>
              <span className={`inline-flex items-center px-2 py-1 text-xs rounded ${
                user?.status === 'active' ? 'bg-success/10 text-success' :
                user?.status === 'trial' ? 'bg-warning/10 text-warning' :
                'bg-error/10 text-error'
              }`}>
                {user?.status === 'active' ? 'Aktiv' :
                 user?.status === 'trial' ? 'Trial' : 'Gesperrt'}
              </span>
            </div>
          </div>
        </section>
        
        {/* Connected Accounts */}
        <section className="bg-bg-surface border border-border-default rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Mail className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-medium text-text-primary">Verbundene E-Mail-Accounts</h2>
          </div>
          
          {accountsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-3">
              {accounts.map((account: any) => (
                <div
                  key={account.id}
                  className="flex items-center justify-between p-4 bg-bg-primary border border-border-default rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      account.provider === 'microsoft' ? 'bg-[#0078d4]/10' : 'bg-white/10'
                    }`}>
                      {account.provider === 'microsoft' ? (
                        <svg className="w-5 h-5 text-[#0078d4]" viewBox="0 0 21 21" fill="currentColor">
                          <path d="M0 0h10v10H0V0zm11 0h10v10H11V0zM0 11h10v10H0V11zm11 0h10v10H11V11z"/>
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      )}
                    </div>
                    <div>
                      <p className="text-text-primary font-medium">{account.providerEmail}</p>
                      <p className="text-xs text-text-muted capitalize">{account.provider}</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      if (confirm('Account wirklich trennen?')) {
                        disconnectMutation.mutate(account.id);
                      }
                    }}
                    className="px-3 py-1.5 text-sm text-error hover:bg-error/10 rounded-lg transition-colors"
                  >
                    Trennen
                  </button>
                </div>
              ))}
              
              {/* Add account buttons */}
              <div className="space-y-3 pt-4">
                {/* Info box when no OAuth configured */}
                {!oauthConfigs.find(c => c.provider === 'microsoft') && !oauthConfigs.find(c => c.provider === 'google') && (
                  <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-warning">
                      <strong>Zuerst OAuth konfigurieren:</strong> Für die Sicherheit deiner Daten benötigt jeder Nutzer seine eigenen OAuth-Credentials. 
                      Konfiguriere unten Google oder Microsoft OAuth, bevor du einen Account verbindest.
                    </p>
                  </div>
                )}
                
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      if (!oauthConfigs.find(c => c.provider === 'microsoft')) {
                        toast.error('Bitte zuerst Microsoft OAuth konfigurieren (siehe unten)');
                        setExpandedProvider('microsoft');
                        return;
                      }
                      connectMicrosoftMutation.mutate();
                    }}
                    disabled={connectMicrosoftMutation.isPending}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors disabled:opacity-50 ${
                      oauthConfigs.find(c => c.provider === 'microsoft')
                        ? 'bg-[#0078d4] text-white hover:bg-[#0078d4]/90'
                        : 'bg-[#0078d4]/30 text-white/50 cursor-not-allowed'
                    }`}
                  >
                    {connectMicrosoftMutation.isPending ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <svg className="w-5 h-5" viewBox="0 0 21 21" fill="currentColor">
                        <path d="M0 0h10v10H0V0zm11 0h10v10H11V0zM0 11h10v10H0V11zm11 0h10v10H11V11z"/>
                      </svg>
                    )}
                    Microsoft 365
                    {!oauthConfigs.find(c => c.provider === 'microsoft') && (
                      <Key className="w-4 h-4 opacity-50" />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      if (!oauthConfigs.find(c => c.provider === 'google')) {
                        toast.error('Bitte zuerst Google OAuth konfigurieren (siehe unten)');
                        setExpandedProvider('google');
                        return;
                      }
                      connectGoogleMutation.mutate();
                    }}
                    disabled={connectGoogleMutation.isPending}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors disabled:opacity-50 ${
                      oauthConfigs.find(c => c.provider === 'google')
                        ? 'bg-white text-gray-700 hover:bg-gray-50'
                        : 'bg-white/30 text-gray-700/50 cursor-not-allowed'
                    }`}
                  >
                    {connectGoogleMutation.isPending ? (
                      <Loader2 className="w-5 h-5 animate-spin text-gray-700" />
                    ) : (
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    )}
                    Gmail
                    {!oauthConfigs.find(c => c.provider === 'google') && (
                      <Key className="w-4 h-4 opacity-50" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
        
        {/* OAuth Configuration */}
        <section className="bg-bg-surface border border-border-default rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <Key className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-medium text-text-primary">OAuth Konfiguration</h2>
            <span className="text-xs px-2 py-0.5 bg-warning/20 text-warning rounded-full">Erforderlich</span>
          </div>
          <p className="text-sm text-text-muted mb-6">
            <strong className="text-text-secondary">Jeder Nutzer benötigt eigene OAuth-Credentials.</strong> Dies garantiert vollständige Isolation deiner Daten — 
            andere Nutzer (auch Konkurrenten) können niemals auf deine E-Mails zugreifen.
          </p>
          
          {oauthConfigsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Google Config */}
              <div className="border border-border-default rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedProvider(expandedProvider === 'google' ? null : 'google')}
                  className="w-full flex items-center justify-between p-4 hover:bg-bg-surface-hover transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                    <div className="text-left">
                      <p className="text-text-primary font-medium">Google OAuth</p>
                      {oauthConfigs.find(c => c.provider === 'google') ? (
                        <p className="text-xs text-success flex items-center gap-1">
                          <Check className="w-3 h-3" /> Konfiguriert
                        </p>
                      ) : (
                        <p className="text-xs text-text-muted">Nicht konfiguriert</p>
                      )}
                    </div>
                  </div>
                  {expandedProvider === 'google' ? (
                    <ChevronUp className="w-5 h-5 text-text-muted" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-text-muted" />
                  )}
                </button>
                
                {expandedProvider === 'google' && (
                  <div className="p-4 border-t border-border-default bg-bg-primary space-y-4">
                    {/* Redirect URI - WICHTIG */}
                    <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                      <p className="text-xs text-text-muted mb-2">
                        <strong className="text-blue-400">Redirect URI</strong> — Diese URL in der Google Cloud Console als "Authorized redirect URI" eintragen:
                      </p>
                      <div className="flex gap-2">
                        <code className="flex-1 px-3 py-2 rounded bg-bg-secondary text-sm text-text-primary overflow-x-auto">
                          {googleRedirectUri}
                        </code>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(googleRedirectUri)}
                          className="px-3 py-2 rounded bg-bg-secondary text-text-secondary hover:bg-bg-tertiary transition-colors"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Instructions toggle */}
                    <button
                      onClick={() => setShowInstructions(showInstructions === 'google' ? null : 'google')}
                      className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
                    >
                      <HelpCircle className="w-4 h-4" />
                      {showInstructions === 'google' ? 'Anleitung ausblenden' : 'Anleitung anzeigen'}
                    </button>
                    
                    {/* Instructions */}
                    {showInstructions === 'google' && googleInstructions && (
                      <div className="p-4 bg-bg-secondary rounded-lg space-y-3">
                        <h4 className="font-medium text-text-primary text-sm">{googleInstructions.title}</h4>
                        <ol className="space-y-2">
                          {googleInstructions.steps.map((step) => (
                            <li key={step.step} className="text-sm text-text-muted">
                              <span className="text-primary font-medium">{step.step}.</span> {step.title}
                              <p className="text-xs mt-1">{step.description}</p>
                              {step.link && (
                                <a href={step.link} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1 mt-1">
                                  <ExternalLink className="w-3 h-3" /> Link öffnen
                                </a>
                              )}
                              {step.copyValue && (
                                <button 
                                  onClick={() => copyToClipboard(step.copyValue!)}
                                  className="text-xs text-primary hover:underline flex items-center gap-1 mt-1"
                                >
                                  <Copy className="w-3 h-3" /> {step.copyValue}
                                </button>
                              )}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                    
                    {/* Form */}
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm text-text-muted mb-1">Client ID</label>
                        <input
                          type="text"
                          value={oauthForms.google?.clientId || ''}
                          onChange={(e) => setOauthForms(prev => ({
                            ...prev,
                            google: { ...prev.google, clientId: e.target.value, clientSecret: prev.google?.clientSecret || '' }
                          }))}
                          placeholder="xxx.apps.googleusercontent.com"
                          className="w-full px-3 py-2 bg-bg-secondary border border-border-default rounded-lg text-text-primary focus:border-primary focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-text-muted mb-1">Client Secret</label>
                        <div className="relative">
                          <input
                            type={showSecrets.google ? 'text' : 'password'}
                            value={oauthForms.google?.clientSecret || ''}
                            onChange={(e) => setOauthForms(prev => ({
                              ...prev,
                              google: { ...prev.google, clientSecret: e.target.value, clientId: prev.google?.clientId || '' }
                            }))}
                            placeholder="GOCSPX-..."
                            className="w-full px-3 py-2 pr-10 bg-bg-secondary border border-border-default rounded-lg text-text-primary focus:border-primary focus:outline-none"
                          />
                          <button
                            type="button"
                            onClick={() => setShowSecrets(prev => ({ ...prev, google: !prev.google }))}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary"
                          >
                            {showSecrets.google ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={() => saveOAuthConfigMutation.mutate({
                            provider: 'google',
                            clientId: oauthForms.google?.clientId || '',
                            clientSecret: oauthForms.google?.clientSecret || '',
                          })}
                          disabled={saveOAuthConfigMutation.isPending || !oauthForms.google?.clientId || !oauthForms.google?.clientSecret}
                          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {saveOAuthConfigMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Speichern'}
                        </button>
                        {oauthConfigs.find(c => c.provider === 'google') && (
                          <button
                            onClick={() => {
                              if (confirm('Google OAuth wirklich löschen?')) {
                                deleteOAuthConfigMutation.mutate('google');
                              }
                            }}
                            disabled={deleteOAuthConfigMutation.isPending}
                            className="px-4 py-2 text-error hover:bg-error/10 rounded-lg transition-colors"
                          >
                            Löschen
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Microsoft Config */}
              <div className="border border-border-default rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedProvider(expandedProvider === 'microsoft' ? null : 'microsoft')}
                  className="w-full flex items-center justify-between p-4 hover:bg-bg-surface-hover transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#0078d4]/10 flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#0078d4]" viewBox="0 0 21 21" fill="currentColor">
                        <path d="M0 0h10v10H0V0zm11 0h10v10H11V0zM0 11h10v10H0V11zm11 0h10v10H11V11z"/>
                      </svg>
                    </div>
                    <div className="text-left">
                      <p className="text-text-primary font-medium">Microsoft OAuth</p>
                      {oauthConfigs.find(c => c.provider === 'microsoft') ? (
                        <p className="text-xs text-success flex items-center gap-1">
                          <Check className="w-3 h-3" /> Konfiguriert
                        </p>
                      ) : (
                        <p className="text-xs text-text-muted">Nicht konfiguriert</p>
                      )}
                    </div>
                  </div>
                  {expandedProvider === 'microsoft' ? (
                    <ChevronUp className="w-5 h-5 text-text-muted" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-text-muted" />
                  )}
                </button>
                
                {expandedProvider === 'microsoft' && (
                  <div className="p-4 border-t border-border-default bg-bg-primary space-y-4">
                    {/* Redirect URI - WICHTIG */}
                    <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                      <p className="text-xs text-text-muted mb-2">
                        <strong className="text-blue-400">Redirect URI</strong> — Diese URL im Azure Portal unter "Authentication → Web → Redirect URIs" eintragen:
                      </p>
                      <div className="flex gap-2">
                        <code className="flex-1 px-3 py-2 rounded bg-bg-secondary text-sm text-text-primary overflow-x-auto">
                          {microsoftRedirectUri}
                        </code>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(microsoftRedirectUri)}
                          className="px-3 py-2 rounded bg-bg-secondary text-text-secondary hover:bg-bg-tertiary transition-colors"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Instructions toggle */}
                    <button
                      onClick={() => setShowInstructions(showInstructions === 'microsoft' ? null : 'microsoft')}
                      className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
                    >
                      <HelpCircle className="w-4 h-4" />
                      {showInstructions === 'microsoft' ? 'Anleitung ausblenden' : 'Anleitung anzeigen'}
                    </button>
                    
                    {/* Instructions */}
                    {showInstructions === 'microsoft' && microsoftInstructions && (
                      <div className="p-4 bg-bg-secondary rounded-lg space-y-3">
                        <h4 className="font-medium text-text-primary text-sm">{microsoftInstructions.title}</h4>
                        <ol className="space-y-2">
                          {microsoftInstructions.steps.map((step) => (
                            <li key={step.step} className="text-sm text-text-muted">
                              <span className="text-primary font-medium">{step.step}.</span> {step.title}
                              <p className="text-xs mt-1">{step.description}</p>
                              {step.link && (
                                <a href={step.link} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1 mt-1">
                                  <ExternalLink className="w-3 h-3" /> Link öffnen
                                </a>
                              )}
                              {step.copyValue && (
                                <button 
                                  onClick={() => copyToClipboard(step.copyValue!)}
                                  className="text-xs text-primary hover:underline flex items-center gap-1 mt-1"
                                >
                                  <Copy className="w-3 h-3" /> {step.copyValue}
                                </button>
                              )}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                    
                    {/* Form */}
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm text-text-muted mb-1">Client ID (Application ID)</label>
                        <input
                          type="text"
                          value={oauthForms.microsoft?.clientId || ''}
                          onChange={(e) => setOauthForms(prev => ({
                            ...prev,
                            microsoft: { ...prev.microsoft, clientId: e.target.value, clientSecret: prev.microsoft?.clientSecret || '' }
                          }))}
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          className="w-full px-3 py-2 bg-bg-secondary border border-border-default rounded-lg text-text-primary focus:border-primary focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-text-muted mb-1">Client Secret</label>
                        <div className="relative">
                          <input
                            type={showSecrets.microsoft ? 'text' : 'password'}
                            value={oauthForms.microsoft?.clientSecret || ''}
                            onChange={(e) => setOauthForms(prev => ({
                              ...prev,
                              microsoft: { ...prev.microsoft, clientSecret: e.target.value, clientId: prev.microsoft?.clientId || '' }
                            }))}
                            placeholder="xxx~xxx..."
                            className="w-full px-3 py-2 pr-10 bg-bg-secondary border border-border-default rounded-lg text-text-primary focus:border-primary focus:outline-none"
                          />
                          <button
                            type="button"
                            onClick={() => setShowSecrets(prev => ({ ...prev, microsoft: !prev.microsoft }))}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary"
                          >
                            {showSecrets.microsoft ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-text-muted mb-1">Tenant (optional)</label>
                        <select
                          value={oauthForms.microsoft?.tenantId || 'common'}
                          onChange={(e) => setOauthForms(prev => ({
                            ...prev,
                            microsoft: { ...prev.microsoft, tenantId: e.target.value, clientId: prev.microsoft?.clientId || '', clientSecret: prev.microsoft?.clientSecret || '' }
                          }))}
                          className="w-full px-3 py-2 bg-bg-secondary border border-border-default rounded-lg text-text-primary focus:border-primary focus:outline-none"
                        >
                          <option value="common">Alle Kontotypen (empfohlen)</option>
                          <option value="organizations">Nur Geschäftskonten</option>
                          <option value="consumers">Nur persönliche Konten</option>
                        </select>
                        <p className="text-xs text-text-muted mt-1">
                          <Info className="w-3 h-3 inline mr-1" />
                          Für Multi-Tenant Apps "Alle Kontotypen" wählen
                        </p>
                      </div>
                      
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={() => saveOAuthConfigMutation.mutate({
                            provider: 'microsoft',
                            clientId: oauthForms.microsoft?.clientId || '',
                            clientSecret: oauthForms.microsoft?.clientSecret || '',
                            tenantId: oauthForms.microsoft?.tenantId || 'common',
                          })}
                          disabled={saveOAuthConfigMutation.isPending || !oauthForms.microsoft?.clientId || !oauthForms.microsoft?.clientSecret}
                          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {saveOAuthConfigMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Speichern'}
                        </button>
                        {oauthConfigs.find(c => c.provider === 'microsoft') && (
                          <button
                            onClick={() => {
                              if (confirm('Microsoft OAuth wirklich löschen?')) {
                                deleteOAuthConfigMutation.mutate('microsoft');
                              }
                            }}
                            disabled={deleteOAuthConfigMutation.isPending}
                            className="px-4 py-2 text-error hover:bg-error/10 rounded-lg transition-colors"
                          >
                            Löschen
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Security note */}
              <div className="p-3 bg-success/5 border border-success/20 rounded-lg flex items-start gap-2">
                <Shield className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                <p className="text-xs text-success">
                  Deine OAuth Secrets werden mit AES-256-GCM verschlüsselt gespeichert. 
                  Nur du hast Zugriff auf deine Credentials.
                </p>
              </div>
            </div>
          )}
        </section>
        
        {/* Display Settings - only show on desktop */}
        {!isMobile && (
          <section className="bg-bg-surface border border-border-default rounded-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <Monitor className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-medium text-text-primary">Darstellung</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-text-muted mb-3">Mail-Vorschau Modus</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setReadingPaneMode('sidebar')}
                    className={`flex flex-col items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                      readingPaneMode === 'sidebar' 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border-default hover:border-border-hover bg-bg-primary'
                    }`}
                  >
                    <div className="w-full aspect-video bg-bg-secondary rounded flex overflow-hidden">
                      {/* Sidebar preview illustration */}
                      <div className="flex-1 p-2 flex gap-1">
                        <div className="w-1/4 bg-bg-tertiary rounded" />
                        <div className="w-1/4 bg-bg-tertiary rounded" />
                      </div>
                      <div className="w-2/5 bg-primary/20 border-l border-primary/30" />
                    </div>
                    <div className="flex items-center gap-2">
                      <PanelRight className={`w-4 h-4 ${readingPaneMode === 'sidebar' ? 'text-primary' : 'text-text-muted'}`} />
                      <span className={`text-sm font-medium ${readingPaneMode === 'sidebar' ? 'text-primary' : 'text-text-primary'}`}>
                        Seitenleiste
                      </span>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setReadingPaneMode('popup')}
                    className={`flex flex-col items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                      readingPaneMode === 'popup' 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border-default hover:border-border-hover bg-bg-primary'
                    }`}
                  >
                    <div className="w-full aspect-video bg-bg-secondary rounded flex items-center justify-center relative overflow-hidden">
                      {/* Popup preview illustration */}
                      <div className="absolute inset-2 p-1 flex gap-1">
                        <div className="w-1/3 bg-bg-tertiary/50 rounded" />
                        <div className="w-1/3 bg-bg-tertiary/50 rounded" />
                        <div className="w-1/3 bg-bg-tertiary/50 rounded" />
                      </div>
                      <div className="relative w-3/4 h-3/4 bg-primary/20 rounded border-2 border-primary/40 shadow-lg" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Maximize2 className={`w-4 h-4 ${readingPaneMode === 'popup' ? 'text-primary' : 'text-text-muted'}`} />
                      <span className={`text-sm font-medium ${readingPaneMode === 'popup' ? 'text-primary' : 'text-text-primary'}`}>
                        Popup
                      </span>
                    </div>
                  </button>
                </div>
                <p className="text-xs text-text-muted mt-2">
                  Auf Mobilgeräten wird immer das Popup verwendet.
                </p>
              </div>
            </div>
          </section>
        )}
        
        {/* AI Provider Settings */}
        <ProviderSettings />
        
        {/* AI Summary Prompt Settings */}
        <SummaryPromptSettings />
        
        {/* Danger Zone */}
        <section className="bg-bg-surface border border-error/20 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-5 h-5 text-error" />
            <h2 className="text-lg font-medium text-error">Gefahrenzone</h2>
          </div>
          
          <p className="text-sm text-text-muted mb-4">
            Wenn du deinen Account löschst, werden alle deine Daten unwiderruflich gelöscht.
          </p>
          
          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 bg-error/10 text-error rounded-lg hover:bg-error/20 transition-colors"
            >
              Account löschen
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <button
                onClick={() => deleteAccountMutation.mutate()}
                disabled={deleteAccountMutation.isPending}
                className="px-4 py-2 bg-error text-white rounded-lg hover:bg-error/90 transition-colors disabled:opacity-50"
              >
                {deleteAccountMutation.isPending ? 'Lösche...' : 'Endgültig löschen'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-text-muted hover:text-text-secondary transition-colors"
              >
                Abbrechen
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
